import { useContext, useEffect, useState } from 'react'
import Radio from '@/app/components/controls/radio'
import { ModalContext } from '@/app/components/modal'
import { FlashMessageContext } from '@/context/flash-message'
import { SubmitButton } from '@/components/button'
import { cn } from '@/util/cn'
import HistogramIcon from '@/app/icons/histogram'
import { derankUser } from '@/api/derank'
import UserContext from '@/context/user-context'

const fieldNames = { accountType: 'accountType' }

const derankOptions = [
    { value: 'unset', label: 'Unset deranking', color: 'rgba(34,197,94,0.85)', text: 'text-white' },
    { value: 'low', label: 'Set low level', color: 'rgba(253,224,71,0.85)', text: 'text-black' },
    { value: 'high', label: 'Set high level', color: 'rgba(239,68,68,0.85)', text: 'text-white' },
]

const DerankUser = () => {
    const { user } = useContext(UserContext)
    const { modal, setModalType } = useContext(ModalContext)
    const { showMessage } = useContext(FlashMessageContext)
    const [result, setResult] = useState<string | null>(null)
    const [accountType, setAccountType] = useState('')
    const [selectedDerank, setSelectedDerank] = useState<'high' | 'low' | 'unset' | null>(null)
    const [formError, setFormError] = useState<string | null>(null)

    const syncableTags =
        modal && 'channel' in modal && modal.channel && 'syncable_tags' in modal.channel
            ? (modal.channel.syncable_tags as Array<{ tag: string }>)
            : []

    useEffect(() => {
        let timeout: NodeJS.Timeout
        if (result === 'success') {
            timeout = setTimeout(() => {
                setModalType(null)
            }, 3000)
        }
        return () => clearTimeout(timeout)
    }, [result, setModalType])

    useEffect(() => {
        if (!accountType && syncableTags.length > 0) {
            if (syncableTags.some((t: { tag: string }) => t.tag.startsWith('giphyderankofficial'))) {
                setAccountType('official')
            } else if (syncableTags.some((t: { tag: string }) => t.tag.startsWith('giphyderankpromotional'))) {
                setAccountType('promotional')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal])

    if (modal?.name !== 'derankUser') {
        return null
    }

    const handleConfirm = async () => {
        setFormError(null)
        if (!selectedDerank) {
            setFormError('Please select a deranking level.')
            return
        }
        if ((selectedDerank === 'high' || selectedDerank === 'low') && !accountType) {
            setFormError('Please select an account type for high or low deranking.')
            return
        }

        setResult('success')
        showMessage({
            message:
                selectedDerank === 'high'
                    ? 'Set to high deranking level'
                    : selectedDerank === 'low'
                      ? 'Set to low deranking level'
                      : 'Deranking unset',
            type: 'success',
        })
        const syncableTags =
            Array.isArray(modal?.channel.syncable_tags) &&
            modal?.channel.syncable_tags.every((tag) => typeof tag.tag === 'string')
                ? modal?.channel.syncable_tags
                : []
        await derankUser(
            modal?.channel?.id ?? 0,
            selectedDerank === 'unset' ? 'promotional' : accountType,
            selectedDerank,
            syncableTags,
            user?.token as string,
            document.cookie
        )
        window.location.reload()
    }

    const hasTag = (tag: string) => syncableTags.some((t: { tag: string }) => t.tag === tag)

    const getIsDisabled = (optValue: string) => {
        if (optValue === 'high') {
            if (accountType === 'official' && hasTag('giphyderankofficialaccounthigh')) return true
            if (accountType === 'promotional' && hasTag('giphyderankpromotionalaccounthigh')) return true
            return false
        }
        if (optValue === 'low') {
            if (accountType === 'official' && hasTag('giphyderankofficialaccountlow')) return true
            if (accountType === 'promotional' && hasTag('giphyderankpromotionalaccountlow')) return true
            return false
        }
        if (optValue === 'unset') {
            // Disable unset if none of the derank tags are present
            const hasAnyDerankTag =
                hasTag('giphyderankofficialaccounthigh') ||
                hasTag('giphyderankofficialaccountlow') ||
                hasTag('giphyderankpromotionalaccounthigh') ||
                hasTag('giphyderankpromotionalaccountlow')
            return !hasAnyDerankTag
        }
        return false
    }

    return result === 'success' ? (
        <div className="grid flex-1 place-items-center">
            <h1>Action completed</h1>
        </div>
    ) : (
        <form
            // eslint-disable-next-line better-tailwindcss/no-unregistered-classes
            className="group flex flex-1 flex-col gap-6 p-4 [&_h4]:font-normal [&_h4]:text-giphyLightGrey"
            onSubmit={(e) => {
                e.preventDefault()
                handleConfirm()
            }}
        >
            <h1>
                Derank <span className="text-giphyBlue">@{modal?.channel.user.username}</span>
            </h1>
            <div className="flex flex-col gap-6">
                <section className="flex flex-col gap-2">
                    <h4>Account Type</h4>
                    <div className="flex gap-6">
                        <Radio
                            id="official"
                            label="Official account"
                            name={fieldNames.accountType}
                            checked={accountType === 'official'}
                            onChange={() => setAccountType('official')}
                            value="official"
                        />
                        <Radio
                            id="promotional"
                            label="Promotional account"
                            name={fieldNames.accountType}
                            checked={accountType === 'promotional'}
                            onChange={() => setAccountType('promotional')}
                            value="promotional"
                        />
                    </div>
                </section>
                <section>
                    <div className="flex justify-between gap-4">
                        {derankOptions.map((opt) => {
                            const isDisabled = getIsDisabled(opt.value)
                            const isHighlighted = isDisabled // Use the same logic as disabled
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`flex-1 rounded-lg border-4 py-6 text-lg font-bold transition-all duration-200 ${
                                        selectedDerank === opt.value ? 'opacity-80' : 'opacity-80 hover:opacity-100'
                                    } ${isHighlighted ? 'ring-4' : ''}`}
                                    style={{
                                        border: `2px solid ${isHighlighted ? opt.color : opt.color}`,
                                        color: isHighlighted
                                            ? opt.color
                                            : selectedDerank === opt.value
                                              ? 'white'
                                              : opt.color,
                                        background: isHighlighted
                                            ? 'rgba(0,0,0,0.7)'
                                            : selectedDerank === opt.value
                                              ? opt.color
                                              : 'transparent',
                                        boxShadow: isHighlighted ? `0 0 0 4px ${opt.color}` : undefined,
                                        transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
                                    }}
                                    onClick={() => setSelectedDerank(opt.value as 'high' | 'low' | 'unset')}
                                >
                                    <span className="flex w-full items-center justify-center gap-2">
                                        <HistogramIcon
                                            filledIndex={opt.value === 'high' ? 2 : opt.value === 'low' ? 1 : 0}
                                            fillColor={isHighlighted ? opt.color : opt.color}
                                        />
                                        <span>{opt.label}</span>
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                </section>
                {formError && <div className="text-sm text-red-500">{formError}</div>}
                <SubmitButton
                    className={cn(
                        'w-full bg-gradient-error group-invalid:pointer-events-none group-invalid:bg-gradient-grey group-invalid:opacity-50'
                    )}
                >
                    Confirm Deranking
                </SubmitButton>
            </div>
        </form>
    )
}

export default DerankUser
